/**
 * RefineCodeView — Refine Code Flow (standalone, wired to backend)
 *
 * Independent HITL workbench/checkpoint stage.
 * Does NOT require Refactor Code to have run first.
 *
 * Flow:
 *   Select SvcGen job (+ optional Refactor plan)
 *     → POST /refine-code/start
 *     → Poll /refine-code/status/{job_id}
 *     → GET /refine-code/{job_id}/workbench
 *     → Workbench: file tree / Java editor / patch+compile panels
 *     → Download or Continue to Build / Package
 *
 * CSS prefix: rcv-* (outer chrome), rw-* (workbench panels — shared with mock)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowLeft, ChevronRight, ChevronDown, Download, ArrowRight,
  CheckCircle2, XCircle, AlertTriangle, Loader2, Info,
  FileCode, Folder, FolderOpen, Code2, GitCompare,
  BookOpen, RotateCcw, Copy, Check, Play, RefreshCw,
  ToggleLeft, ToggleRight,
} from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './RefineCodeView.css'

// ─── Auth helpers (same pattern as RefactorCodeView) ──────────────────────────

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('modernizeit_token')
  const headers = { ...extra }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} }
}

// ─── Root component ───────────────────────────────────────────────────────────

export default function RefineCodeView({
  project,
  onBack,
  onGoToJavaPkg,
  onOpenSettings,
  onOpenHelp,
  userProfile = {},
}) {
  const apiBase     = getAPIBaseUrl()
  const accountId   = project?.account || ''
  const projectName = project?.name || ''

  // phase: 'select' | 'starting' | 'running' | 'workbench'
  const [phase, setPhase]           = useState('select')
  const [jobId, setJobId]           = useState(null)
  const [pollError, setPollError]   = useState(null)
  const [jobStatus, setJobStatus]   = useState(null)   // raw status from /status
  const [workbench, setWorkbench]   = useState(null)   // /workbench response
  const [wbLoading, setWbLoading]   = useState(false)
  const pollRef = useRef(null)

  // ── Poll job status ──────────────────────────────────────────────────────────

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }, [])

  useEffect(() => {
    if (phase !== 'running' || !jobId) return
    const poll = async () => {
      try {
        const r = await fetch(`${apiBase}/refine-code/status/${encodeURIComponent(jobId)}`, {
          headers: getAuthHeaders(),
        })
        if (!r.ok) return
        const d = await r.json()
        setJobStatus(d)
        if (d.status === 'completed' || d.status === 'succeeded') {
          stopPoll()
          loadWorkbench(jobId)
        } else if (d.status === 'failed' || d.status === 'compile_error') {
          stopPoll()
          // Still load workbench so user can see compile errors
          loadWorkbench(jobId)
        }
      } catch (e) {
        setPollError(e.message)
      }
    }
    poll()
    pollRef.current = setInterval(poll, 3000)
    return stopPoll
  }, [phase, jobId, apiBase, stopPoll])

  // ── Load workbench data ──────────────────────────────────────────────────────

  const loadWorkbench = useCallback(async (jid) => {
    setWbLoading(true)
    try {
      const r = await fetch(`${apiBase}/refine-code/${encodeURIComponent(jid)}/workbench`, {
        headers: getAuthHeaders(),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setWorkbench(d)
      setPhase('workbench')
    } catch (e) {
      setPollError(`Workbench load failed: ${e.message}`)
      setPhase('workbench')  // show workbench anyway, will display error
    } finally {
      setWbLoading(false)
    }
  }, [apiBase])

  // ── Start job callback (called by SelectPhase) ───────────────────────────────

  const handleStarted = useCallback((newJobId) => {
    setJobId(newJobId)
    setPhase('running')
    setPollError(null)
    setJobStatus(null)
    setWorkbench(null)
  }, [])

  // ── Continue to Build / Package ──────────────────────────────────────────────

  const handleContinue = useCallback(() => {
    const svcgenIds = workbench?.summary?.svcgen_job_id
      ? [workbench.summary.svcgen_job_id]
      : []
    onGoToJavaPkg?.(svcgenIds, jobId)
  }, [workbench, jobId, onGoToJavaPkg])

  // ── Download refined code ────────────────────────────────────────────────────

  const handleDownload = useCallback(async () => {
    if (!jobId) return
    const url = `${apiBase}/refine-code/results/download/${encodeURIComponent(jobId)}/refined-code-package`
    try {
      const r = await fetch(url, { headers: getAuthHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const cd = r.headers.get('Content-Disposition') || ''
      const m = cd.match(/filename="([^"]+)"/)
      a.download = m ? m[1] : `refined_code_${jobId}.zip`
      a.href = href
      a.click()
      URL.revokeObjectURL(href)
    } catch (e) {
      alert(`Download failed: ${e.message}`)
    }
  }, [apiBase, jobId])

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="rcv-root">
      {/* Top chrome */}
      <div className="rcv-topchrome">
        <button className="rcv-back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rcv-breadcrumb">
          <span>{projectName}</span>
          <ChevronRight size={13} className="rcv-breadcrumb-sep" />
          <span className="rcv-breadcrumb-active">Refine Code</span>
        </div>
      </div>

      {/* Phase views */}
      {phase === 'select' && (
        <SelectPhase
          apiBase={apiBase}
          accountId={accountId}
          projectName={projectName}
          onStarted={handleStarted}
        />
      )}

      {phase === 'starting' && (
        <CenteredSpinner label="Creating Refine Code job…" />
      )}

      {phase === 'running' && (
        <RunningPhase jobStatus={jobStatus} error={pollError} wbLoading={wbLoading} />
      )}

      {phase === 'workbench' && (
        <WorkbenchPhase
          jobId={jobId}
          apiBase={apiBase}
          workbench={workbench}
          jobStatus={jobStatus}
          onDownload={handleDownload}
          onContinue={handleContinue}
          onReload={() => loadWorkbench(jobId)}
        />
      )}
    </div>
  )
}

// ─── SelectPhase ─────────────────────────────────────────────────────────────

function SelectPhase({ apiBase, accountId, projectName, onStarted }) {
  const [svcgenJobs,    setSvcgenJobs]    = useState([])
  const [svcgenLoading, setSvcgenLoading] = useState(false)
  const [svcgenError,   setSvcgenError]   = useState(null)
  const [selectedSvcgen, setSelectedSvcgen] = useState(null)

  const [useRefactorPlan, setUseRefactorPlan] = useState(false)
  const [rfctJobs,    setRfctJobs]    = useState([])
  const [rfctLoading, setRfctLoading] = useState(false)
  const [selectedRfct, setSelectedRfct] = useState(null)

  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState(null)
  const [showAllJobs, setShowAllJobs] = useState(false)

  // Load SvcGen jobs
  useEffect(() => {
    if (!accountId) return
    setSvcgenLoading(true)
    setSvcgenError(null)
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    const params = new URLSearchParams({ account_id: accountId, user_id: userId })
    if (projectName) params.set('application_name', projectName)
    fetch(`${apiBase}/refactor-code/available-svcgen-jobs?${params}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => {
        setSvcgenJobs(Array.isArray(data) ? data : [])
        if (Array.isArray(data) && data.length > 0) setSelectedSvcgen(data[0].job_id)
      })
      .catch(e => setSvcgenError(String(e)))
      .finally(() => setSvcgenLoading(false))
  }, [apiBase, accountId, projectName])

  // Load Refactor Code plans when toggle enabled
  useEffect(() => {
    if (!useRefactorPlan || !accountId) return
    setRfctLoading(true)
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    const params = new URLSearchParams({ account_id: accountId, user_id: userId })
    fetch(`${apiBase}/refine-code/available-refactor-jobs?${params}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => {
        setRfctJobs(Array.isArray(data) ? data : [])
        if (Array.isArray(data) && data.length > 0) setSelectedRfct(data[0].job_id)
      })
      .catch(() => setRfctJobs([]))
      .finally(() => setRfctLoading(false))
  }, [useRefactorPlan, apiBase, accountId])

  const handleStart = async () => {
    if (!selectedSvcgen) return
    setStarting(true)
    setStartError(null)
    const storedUser = getStoredUser()
    const userId = storedUser.user_id || storedUser.id || ''
    const body = {
      scout_account_id: accountId,
      application_name: projectName,
      user_id: userId,
      svcgen_job_id: selectedSvcgen,
    }
    if (useRefactorPlan && selectedRfct) {
      body.refactor_job_id = selectedRfct
    }
    try {
      const r = await fetch(`${apiBase}/refine-code/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      if (!d.job_id) throw new Error('No job_id returned')
      onStarted(d.job_id)
    } catch (e) {
      setStartError(e.message)
      setStarting(false)
    }
  }

  const visibleJobs = showAllJobs ? svcgenJobs : svcgenJobs.slice(0, 6)
  const canStart = !!selectedSvcgen && !starting

  return (
    <div className="rcv-select-phase">
      <div className="rcv-select-card">
        <div className="rcv-select-header">
          <Code2 size={20} className="rcv-select-icon" />
          <div>
            <div className="rcv-select-title">Refine Code</div>
            <div className="rcv-select-sub">
              Select a completed Build Services run to create a refinement checkpoint
            </div>
          </div>
        </div>

        {/* SvcGen job selector */}
        <div className="rcv-select-section">
          <div className="rcv-select-label">
            Build Services output
            {svcgenLoading && <Loader2 size={12} className="rcv-spin" />}
          </div>

          {svcgenError && (
            <div className="rcv-select-error">
              <AlertTriangle size={13} /> {svcgenError}
            </div>
          )}

          {!svcgenLoading && !svcgenError && svcgenJobs.length === 0 && (
            <div className="rcv-select-empty">
              No completed Build Services runs found for this project.
              Run Build Services first, then return here.
            </div>
          )}

          {svcgenJobs.length > 0 && (
            <div className="rcv-job-list">
              {visibleJobs.map(j => (
                <button
                  key={j.job_id}
                  className={`rcv-job-row ${selectedSvcgen === j.job_id ? 'selected' : ''}`}
                  onClick={() => setSelectedSvcgen(j.job_id)}
                >
                  <div className="rcv-job-row-check">
                    {selectedSvcgen === j.job_id
                      ? <CheckCircle2 size={15} />
                      : <div className="rcv-job-row-circle" />}
                  </div>
                  <div className="rcv-job-row-label">{j.label || j.job_id}</div>
                  <div className="rcv-job-row-id">…{j.job_id.slice(-8)}</div>
                </button>
              ))}
              {svcgenJobs.length > 6 && (
                <button
                  className="rcv-show-more-btn"
                  onClick={() => setShowAllJobs(v => !v)}
                >
                  {showAllJobs
                    ? 'Show fewer'
                    : `Show ${svcgenJobs.length - 6} older run${svcgenJobs.length - 6 !== 1 ? 's' : ''} ▼`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Optional Refactor Code plan */}
        <div className="rcv-select-section">
          <div className="rcv-toggle-row">
            <button
              className="rcv-toggle-btn"
              onClick={() => { setUseRefactorPlan(v => !v); setSelectedRfct(null) }}
            >
              {useRefactorPlan
                ? <ToggleRight size={20} className="rcv-toggle-on" />
                : <ToggleLeft size={20} className="rcv-toggle-off" />}
            </button>
            <div className="rcv-toggle-text">
              <div className="rcv-toggle-label">Apply Refactor Code plan</div>
              <div className="rcv-toggle-sub">
                Optional. If a finalized Refactor Code analysis exists, apply its
                confirmed patches during refinement.
              </div>
            </div>
          </div>

          {useRefactorPlan && (
            <div className="rcv-rfct-select">
              {rfctLoading && (
                <div className="rcv-rfct-loading">
                  <Loader2 size={12} className="rcv-spin" /> Loading Refactor Code plans…
                </div>
              )}
              {!rfctLoading && rfctJobs.length === 0 && (
                <div className="rcv-select-empty">
                  No finalized Refactor Code plans found. Refine Code will run
                  in compile-only mode.
                </div>
              )}
              {rfctJobs.length > 0 && (
                <div className="rcv-job-list">
                  {rfctJobs.map(j => (
                    <button
                      key={j.job_id}
                      className={`rcv-job-row ${selectedRfct === j.job_id ? 'selected' : ''}`}
                      onClick={() => setSelectedRfct(j.job_id)}
                    >
                      <div className="rcv-job-row-check">
                        {selectedRfct === j.job_id
                          ? <CheckCircle2 size={15} />
                          : <div className="rcv-job-row-circle" />}
                      </div>
                      <div className="rcv-job-row-label">{j.label || j.job_id}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {startError && (
          <div className="rcv-start-error">
            <AlertTriangle size={14} /> {startError}
          </div>
        )}

        <div className="rcv-select-actions">
          <button
            className={`rcv-start-btn ${canStart ? '' : 'disabled'}`}
            onClick={handleStart}
            disabled={!canStart}
          >
            {starting
              ? <><Loader2 size={14} className="rcv-spin" /> Starting…</>
              : <><Play size={14} /> Start Refine Code</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── RunningPhase ─────────────────────────────────────────────────────────────

function RunningPhase({ jobStatus, error, wbLoading }) {
  const status = jobStatus?.status || 'running'
  const phase  = jobStatus?.current_phase || null

  const phaseLabels = {
    'phase_1': 'Setting up workspace…',
    'phase_2': 'Loading refinement plan…',
    'phase_3': 'Copying Java files to S3…',
    'phase_4': 'Applying patches…',
    'phase_5': 'Running Maven compile…',
    'phase_6': 'Packaging refined code…',
    'phase_7': 'Writing final artifacts…',
  }
  const phaseLabel = (phase && phaseLabels[phase]) || 'Working…'

  return (
    <div className="rcv-running-phase">
      <div className="rcv-running-card">
        {wbLoading
          ? <>
              <Loader2 size={32} className="rcv-running-spinner" />
              <div className="rcv-running-title">Loading workbench…</div>
            </>
          : <>
              <Loader2 size={32} className="rcv-running-spinner" />
              <div className="rcv-running-title">Refine Code is running</div>
              <div className="rcv-running-phase-label">{phaseLabel}</div>
              {error && (
                <div className="rcv-running-error">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}
            </>}
      </div>
    </div>
  )
}

// ─── WorkbenchPhase ───────────────────────────────────────────────────────────

function WorkbenchPhase({ jobId, apiBase, workbench, jobStatus, onDownload, onContinue, onReload }) {
  const [activeFile,   setActiveFile]   = useState(null)
  const [fileContent,  setFileContent]  = useState(null)
  const [fileLoading,  setFileLoading]  = useState(false)
  const [fileError,    setFileError]    = useState(null)
  const [leftTab,      setLeftTab]      = useState('Files')
  const [rightTab,     setRightTab]     = useState('Compile')
  const [copied,       setCopied]       = useState(false)
  const [patchReport,  setPatchReport]  = useState(null)
  const [compileReport, setCompileReport] = useState(null)

  const compileStatus = workbench?.compile_status || jobStatus?.compile_passed === true ? 'PASSED'
    : jobStatus?.compile_passed === false ? 'FAILED' : 'UNKNOWN'

  const summary = workbench?.summary || {}
  const files   = workbench?.files   || []
  const appName = workbench?.app_name || ''

  // Load reports once
  useEffect(() => {
    if (!jobId || !apiBase) return
    const load = async (filename, setter) => {
      try {
        const r = await fetch(`${apiBase}/refine-code/results/json/${encodeURIComponent(jobId)}/${filename}`, {
          headers: getAuthHeaders(),
        })
        if (r.ok) setter(await r.json())
      } catch { /* non-fatal */ }
    }
    load('patch_report.json', setPatchReport)
    load('compile_report.json', setCompileReport)
  }, [jobId, apiBase])

  // Load file content when activeFile changes
  useEffect(() => {
    if (!activeFile || !jobId) return
    setFileLoading(true)
    setFileContent(null)
    setFileError(null)
    const params = new URLSearchParams({ path: activeFile.path })
    fetch(`${apiBase}/refine-code/${encodeURIComponent(jobId)}/file?${params}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(d => setFileContent(d.content || ''))
      .catch(e => setFileError(String(e)))
      .finally(() => setFileLoading(false))
  }, [activeFile, jobId, apiBase])

  const handleCopy = () => {
    if (!fileContent) return
    navigator.clipboard.writeText(fileContent).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleSelectFile = (f) => {
    setActiveFile(f)
    setRightTab(f.status === 'modified' ? 'Patches' : 'Compile')
  }

  const actualCompileStatus = compileReport
    ? (compileReport.compile_passed ? 'PASSED' : 'FAILED')
    : workbench?.compile_status || 'UNKNOWN'

  return (
    <div className="rw-root">
      {/* Top Summary Bar */}
      <div className="rw-topbar">
        <div className="rw-topbar-left">
          <div className="rw-topbar-title">
            <Code2 size={16} className="rw-topbar-icon" />
            Refine Code Checkpoint
          </div>
          <div className="rw-topbar-app">{appName}</div>
        </div>

        <div className="rw-topbar-center">
          <CompileBadge status={actualCompileStatus} />
          <SummaryCount label="Applied"      value={summary.applied      ?? 0} variant="applied"  />
          <SummaryCount label="Needs Review" value={summary.needs_review ?? 0} variant="review"   />
          <SummaryCount label="Files Changed" value={summary.files_changed ?? 0} variant="neutral" />
          <SummaryCount label="Total Files"  value={summary.total_files   ?? files.length} variant="neutral" />
        </div>

        <div className="rw-topbar-right">
          <button className="rw-btn rw-btn-ghost" onClick={onReload} title="Reload workbench">
            <RefreshCw size={13} />
          </button>
          <button className="rw-btn rw-btn-secondary" onClick={onDownload}>
            <Download size={14} /> Download Refined Code
          </button>
          <button className="rw-btn rw-btn-primary" onClick={onContinue}>
            Continue to Build / Package <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Three-column workspace */}
      <div className="rw-workspace">
        {/* Left panel */}
        <div className="rw-left-panel">
          <div className="rw-panel-tabs">
            {['Files', 'Changes'].map(t => (
              <button
                key={t}
                className={`rw-panel-tab ${leftTab === t ? 'active' : ''}`}
                onClick={() => setLeftTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="rw-panel-content">
            {leftTab === 'Files' && (
              <FileTree files={files} activeFile={activeFile} onSelect={handleSelectFile} />
            )}
            {leftTab === 'Changes' && (
              <ChangesList patchReport={patchReport} />
            )}
          </div>
        </div>

        {/* Center editor */}
        <div className="rw-center-panel">
          {!activeFile && (
            <div className="rw-editor-placeholder">
              <FileCode size={40} className="rw-editor-placeholder-icon" />
              <span>Select a file from the panel on the left</span>
            </div>
          )}
          {activeFile && (
            <>
              <div className="rw-editor-tabbar">
                <div className="rw-editor-tab active">
                  <FileCode size={13} />
                  <span>{activeFile.path.split('/').pop()}</span>
                  {activeFile.status === 'modified' && (
                    <span className="rw-editor-tab-modified" />
                  )}
                </div>
                <div className="rw-editor-tabbar-actions">
                  <button className="rw-editor-action" onClick={handleCopy}>
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                  </button>
                </div>
              </div>

              <div className="rw-editor-body">
                {fileLoading && (
                  <div className="rcv-file-loading">
                    <Loader2 size={18} className="rcv-spin" /> Loading…
                  </div>
                )}
                {fileError && (
                  <div className="rcv-file-error">
                    <AlertTriangle size={14} /> {fileError}
                  </div>
                )}
                {!fileLoading && !fileError && fileContent !== null && (
                  <JavaEditor content={fileContent} />
                )}
              </div>
            </>
          )}
        </div>

        {/* Right panel */}
        <div className="rw-right-panel">
          <div className="rw-panel-tabs">
            {['Compile', 'Patches'].map(t => (
              <button
                key={t}
                className={`rw-panel-tab ${rightTab === t ? 'active' : ''}`}
                onClick={() => setRightTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="rw-panel-content">
            {rightTab === 'Compile' && (
              <CompilePanel compileReport={compileReport} jobId={jobId} apiBase={apiBase} />
            )}
            {rightTab === 'Patches' && (
              <PatchPanel patchReport={patchReport} activeFile={activeFile} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── File Tree ────────────────────────────────────────────────────────────────

function FileTree({ files, activeFile, onSelect }) {
  // Group by first path segment (folder)
  const grouped = {}
  for (const f of files) {
    const parts = f.path.split('/')
    const folder = parts.length > 1 ? parts[0] : '(root)'
    if (!grouped[folder]) grouped[folder] = []
    grouped[folder].push({ ...f, filename: parts[parts.length - 1] })
  }

  const [expanded, setExpanded] = useState(() => {
    const e = {}
    Object.keys(grouped).forEach(k => { e[k] = true })
    return e
  })

  // Sync expansion state when grouped changes
  useEffect(() => {
    setExpanded(prev => {
      const next = { ...prev }
      Object.keys(grouped).forEach(k => { if (!(k in next)) next[k] = true })
      return next
    })
  }, [files.length])

  if (files.length === 0) {
    return (
      <div className="rcv-empty-panel">
        <FileCode size={24} />
        <span>No files in workspace yet</span>
      </div>
    )
  }

  return (
    <div className="rw-file-tree">
      {Object.entries(grouped).map(([folder, items]) => (
        <div key={folder} className="rw-tree-folder">
          <button
            className="rw-tree-folder-row"
            onClick={() => setExpanded(p => ({ ...p, [folder]: !p[folder] }))}
          >
            {expanded[folder]
              ? <FolderOpen size={14} className="rw-folder-icon" />
              : <Folder    size={14} className="rw-folder-icon" />}
            <span className="rw-tree-folder-name">{folder}/</span>
            {expanded[folder]
              ? <ChevronDown  size={12} className="rw-chevron" />
              : <ChevronRight size={12} className="rw-chevron" />}
          </button>
          {expanded[folder] && items.map(f => (
            <button
              key={f.path}
              className={`rw-tree-file-row ${activeFile?.path === f.path ? 'active' : ''}`}
              onClick={() => onSelect(f)}
            >
              <FileCode size={13} className="rw-file-icon" />
              <span className="rw-tree-file-name">{f.filename}</span>
              {f.status === 'modified' && (
                <span className="rw-file-badge rw-file-badge--modified">M</span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

// ─── Changes List ─────────────────────────────────────────────────────────────

function ChangesList({ patchReport }) {
  if (!patchReport) {
    return (
      <div className="rcv-empty-panel">
        <GitCompare size={22} />
        <span>No patch report available</span>
      </div>
    )
  }

  const applied = patchReport.results?.filter(r => r.status === 'applied') || []
  const review  = patchReport.results?.filter(r => r.status !== 'applied') || []

  return (
    <div className="rw-changes-list">
      {applied.length > 0 && (
        <div className="rw-changes-section">
          <div className="rw-changes-section-header rw-changes-section-header--applied">
            <CheckCircle2 size={13} /> Applied ({applied.length})
          </div>
          {applied.map(r => (
            <div key={r.recommendation_id} className="rw-change-item rw-change-item--applied">
              <div className="rw-change-smell">{r.smell_type}</div>
              <div className="rw-change-title">{r.reason || r.recommendation_id}</div>
              <div className="rw-change-file">{(r.target_file || '').split('/').pop()}</div>
            </div>
          ))}
        </div>
      )}
      {review.length > 0 && (
        <div className="rw-changes-section">
          <div className="rw-changes-section-header rw-changes-section-header--review">
            <AlertTriangle size={13} /> Needs Review ({review.length})
          </div>
          {review.map(r => (
            <div key={r.recommendation_id} className="rw-change-item rw-change-item--review">
              <div className="rw-change-smell">{r.smell_type}</div>
              <div className="rw-change-title">{r.reason || r.recommendation_id}</div>
            </div>
          ))}
        </div>
      )}
      {applied.length === 0 && review.length === 0 && (
        <div className="rcv-empty-panel">
          <Info size={22} />
          <span>No patches were applied (compile-only run)</span>
        </div>
      )}
    </div>
  )
}

// ─── Compile Panel ────────────────────────────────────────────────────────────

function CompilePanel({ compileReport, jobId, apiBase }) {
  const passed = compileReport?.compile_passed
  const errors = compileReport?.errors_preview || []
  const tail   = compileReport?.maven_output_tail || ''

  if (!compileReport) {
    return (
      <div className="rcv-empty-panel">
        <Loader2 size={22} className="rcv-spin" />
        <span>Loading compile report…</span>
      </div>
    )
  }

  return (
    <div className="rw-compile-panel">
      <div className={`rw-compile-result ${passed ? 'rw-compile-result--passed' : 'rw-compile-result--failed'}`}>
        {passed
          ? <CheckCircle2 size={18} />
          : <XCircle size={18} />}
        <div>
          <div className="rw-compile-result-title">
            Maven Compile {passed ? 'Passed' : 'Failed'}
          </div>
          <div className="rw-compile-result-sub">
            {compileReport.error_count ?? errors.length} error{(compileReport.error_count ?? errors.length) !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="rcv-compile-errors">
          {errors.map((e, i) => (
            <div key={i} className="rcv-compile-error-row">
              <span className="rcv-compile-error-file">{e.file || 'unknown'}:{e.line || 0}</span>
              <span className="rcv-compile-error-msg">{e.message || String(e)}</span>
            </div>
          ))}
        </div>
      )}

      {tail && (
        <div className="rw-detail-block">
          <div className="rw-detail-label">Maven Output</div>
          <div className="rw-compile-log">
            <pre>{tail}</pre>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Patch Panel ─────────────────────────────────────────────────────────────

function PatchPanel({ patchReport, activeFile }) {
  if (!patchReport) {
    return (
      <div className="rcv-empty-panel">
        <GitCompare size={22} />
        <span>No patch report available</span>
      </div>
    )
  }

  const summary = patchReport.summary || {}
  const results = patchReport.results || []

  // Filter to active file if one is selected
  const fileResults = activeFile
    ? results.filter(r =>
        r.target_file === activeFile.path ||
        r.target_file === activeFile.filename ||
        (r.target_file === '<all>') ||
        (activeFile.path || '').endsWith(r.target_file || '')
      )
    : results

  return (
    <div className="rw-patch-detail">
      {/* Summary counts */}
      <div className="rcv-patch-summary">
        <div className="rcv-patch-count rcv-patch-count--applied">
          <span>{summary.applied ?? 0}</span> applied
        </div>
        <div className="rcv-patch-count rcv-patch-count--annotated">
          <span>{summary.annotated ?? 0}</span> annotated
        </div>
        <div className="rcv-patch-count rcv-patch-count--review">
          <span>{summary.skipped_manual_only ?? 0}</span> needs review
        </div>
      </div>

      <div className="rw-patch-summary-divider">
        {activeFile ? `Patches for ${activeFile.path.split('/').pop()}` : 'All Patches'}
      </div>

      {fileResults.length === 0 && (
        <div className="rcv-empty-panel" style={{ padding: '16px 14px' }}>
          <span>No patches for this file</span>
        </div>
      )}

      {fileResults.map(r => (
        <div
          key={r.recommendation_id}
          className={`rw-patch-row rw-patch-row--${r.status === 'applied' ? 'applied' : 'review'}`}
        >
          <div className="rw-patch-row-id">{r.recommendation_id}</div>
          <div className="rw-patch-row-smell">{r.smell_type}</div>
          <div className="rw-patch-row-status">
            {r.status === 'applied' ? 'Applied' : r.status === 'annotated' ? 'Annotated' : 'No match'}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Java Editor ─────────────────────────────────────────────────────────────

function JavaEditor({ content }) {
  const lines = (content || '').split('\n')
  return (
    <div className="rw-editor-gutter-wrap">
      <div className="rw-editor-gutter">
        {lines.map((_, i) => (
          <div key={i} className="rw-gutter-line">{i + 1}</div>
        ))}
      </div>
      <div className="rw-editor-code">
        {lines.map((line, i) => (
          <div key={i} className="rw-code-line">
            <span className={`rw-code-text ${isCommentLine(line) ? 'rw-code-comment' : ''}`}>
              {tokenizeLine(line)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function isCommentLine(line) {
  const t = line.trimStart()
  return t.startsWith('//') || t.startsWith('*') || t.startsWith('/**') || t.startsWith('/*')
}

function tokenizeLine(line) {
  if (isCommentLine(line)) return line

  const ANNOTATION_RE = /(@\w+)/g
  const KEYWORD_RE    = /\b(public|private|protected|class|interface|enum|import|package|return|new|void|null|true|false|if|else|final|static|extends|implements|throws|this|super|abstract|synchronized|default)\b/g
  const STRING_RE     = /("(?:[^"\\]|\\.)*")/g

  const allMatches = []
  let m
  const strRe = new RegExp(STRING_RE.source, 'g')
  while ((m = strRe.exec(line)) !== null)
    allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'string', text: m[0] })

  const annRe = new RegExp(ANNOTATION_RE.source, 'g')
  while ((m = annRe.exec(line)) !== null) {
    if (!allMatches.some(a => m.index >= a.start && m.index < a.end))
      allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'annotation', text: m[0] })
  }

  const kwRe = new RegExp(KEYWORD_RE.source, 'g')
  while ((m = kwRe.exec(line)) !== null) {
    if (!allMatches.some(a => m.index >= a.start && m.index < a.end))
      allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'keyword', text: m[0] })
  }

  allMatches.sort((a, b) => a.start - b.start)
  const result = []
  let cursor = 0
  for (const tok of allMatches) {
    if (tok.start > cursor) result.push(<span key={cursor}>{line.slice(cursor, tok.start)}</span>)
    result.push(<span key={tok.start} className={`rw-tok-${tok.type}`}>{tok.text}</span>)
    cursor = tok.end
  }
  if (cursor < line.length) result.push(<span key={cursor}>{line.slice(cursor)}</span>)
  return result.length ? result : line
}

// ─── Shared small components ──────────────────────────────────────────────────

function CompileBadge({ status }) {
  if (status === 'PASSED') {
    return (
      <span className="rw-compile-badge rw-compile-passed">
        <CheckCircle2 size={13} /> Compile Passed
      </span>
    )
  }
  if (status === 'FAILED') {
    return (
      <span className="rw-compile-badge rw-compile-failed">
        <XCircle size={13} /> Compile Failed
      </span>
    )
  }
  return (
    <span className="rw-compile-badge rw-compile-unknown">
      <Info size={13} /> Compile {status}
    </span>
  )
}

function SummaryCount({ label, value, variant }) {
  return (
    <div className={`rw-summary-count rw-summary-count--${variant}`}>
      <span className="rw-summary-count-value">{value}</span>
      <span className="rw-summary-count-label">{label}</span>
    </div>
  )
}

function CenteredSpinner({ label }) {
  return (
    <div className="rcv-centered-spinner">
      <Loader2 size={28} className="rcv-spin" />
      {label && <div className="rcv-centered-spinner-label">{label}</div>}
    </div>
  )
}
